import { useState, useEffect, type ChangeEvent } from 'react'

const API_BASE = import.meta.env.PROD
  ? 'https://derm-api.brian-mabry-edwards.workers.dev'
  : ''

interface Patient {
  id: string
  first_name: string
  last_name: string
  date_of_birth?: string
}

class NotImplementedError extends Error {
  constructor(feature: string) {
    super(`NOT IMPLEMENTED: ${feature}`)
    this.name = 'NotImplementedError'
  }
}

export default function Upload() {
  const [files, setFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Patient selection
  const [patients, setPatients] = useState<Patient[]>([])
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [patientSearch, setPatientSearch] = useState('')
  const [isLoadingPatients, setIsLoadingPatients] = useState(false)
  const [showCreatePatient, setShowCreatePatient] = useState(false)

  // New patient form
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [isCreatingPatient, setIsCreatingPatient] = useState(false)

  // Load patients on mount and when search changes
  useEffect(() => {
    const fetchPatients = async () => {
      setIsLoadingPatients(true)
      try {
        const response = await fetch(`${API_BASE}/api/v1/patients`)
        if (response.ok) {
          const data = await response.json()
          setPatients(data.patients || [])
        }
      } catch (err) {
        console.error('Failed to fetch patients:', err)
      } finally {
        setIsLoadingPatients(false)
      }
    }
    fetchPatients()
  }, [])

  // Filter patients based on search
  const filteredPatients = patients.filter((p) => {
    const search = patientSearch.toLowerCase()
    return (
      p.first_name.toLowerCase().includes(search) ||
      p.last_name.toLowerCase().includes(search)
    )
  })

  const handleCreatePatient = async () => {
    if (!newFirstName.trim() || !newLastName.trim()) {
      setError('First name and last name are required')
      return
    }

    setIsCreatingPatient(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/v1/patients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: newFirstName.trim(),
          last_name: newLastName.trim(),
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Failed to create patient: ${response.status}`)
      }

      const data = await response.json()
      const newPatient = data.patient as Patient

      // Add to list and select
      setPatients((prev) => [newPatient, ...prev])
      setSelectedPatient(newPatient)
      setShowCreatePatient(false)
      setNewFirstName('')
      setNewLastName('')
      setSuccess(`Patient "${newPatient.first_name} ${newPatient.last_name}" created`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create patient'
      setError(message)
    } finally {
      setIsCreatingPatient(false)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || [])
    setFiles((prev) => [...prev, ...newFiles])
    setError(null)
  }

  const handleUpload = async () => {
    if (files.length === 0) return
    if (!selectedPatient) {
      setError('Please select a patient before uploading')
      return
    }

    setIsUploading(true)
    setError(null)

    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('image', file)
        formData.append('patient_id', selectedPatient.id)

        const response = await fetch(`${API_BASE}/api/v1/images/upload`, {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new NotImplementedError(
            `POST /api/v1/images/upload returned ${response.status}: ${data.error || 'Unknown error'}`
          )
        }
      }

      // Success
      const count = files.length
      setFiles([])
      setSuccess(`Successfully uploaded ${count} image${count > 1 ? 's' : ''} for ${selectedPatient.first_name} ${selectedPatient.last_name}`)
      setTimeout(() => setSuccess(null), 5000)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed'
      setError(message)
      console.error('Upload error:', err)
    } finally {
      setIsUploading(false)
    }
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload Images</h1>
        <p className="mt-1 text-gray-600">
          Add new patient images to the database for future matching
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
          <h3 className="text-red-800 font-bold text-lg">ERROR</h3>
          <pre className="text-red-700 text-sm mt-2 whitespace-pre-wrap font-mono">{error}</pre>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
          <p className="text-green-800 font-medium">{success}</p>
        </div>
      )}

      {/* Patient Selection */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Select Patient</h2>

        {selectedPatient ? (
          <div className="flex items-center justify-between p-3 bg-primary-50 border border-primary-200 rounded-md">
            <div>
              <p className="font-medium text-primary-900">
                {selectedPatient.first_name} {selectedPatient.last_name}
              </p>
              {selectedPatient.date_of_birth && (
                <p className="text-sm text-primary-700">DOB: {selectedPatient.date_of_birth}</p>
              )}
            </div>
            <button
              onClick={() => setSelectedPatient(null)}
              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Search existing patients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search existing patients
              </label>
              <input
                type="text"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                placeholder="Type to search by name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Patient list */}
            {isLoadingPatients ? (
              <p className="text-gray-500 text-sm">Loading patients...</p>
            ) : filteredPatients.length > 0 ? (
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                  >
                    <p className="font-medium text-gray-900">
                      {patient.first_name} {patient.last_name}
                    </p>
                    {patient.date_of_birth && (
                      <p className="text-sm text-gray-500">DOB: {patient.date_of_birth}</p>
                    )}
                  </button>
                ))}
              </div>
            ) : patients.length === 0 ? (
              <p className="text-gray-500 text-sm">No patients yet. Create one below.</p>
            ) : (
              <p className="text-gray-500 text-sm">No patients match your search.</p>
            )}

            {/* Create new patient toggle */}
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={() => setShowCreatePatient(!showCreatePatient)}
                className="text-primary-600 hover:text-primary-800 text-sm font-medium"
              >
                {showCreatePatient ? '− Cancel' : '+ Create new patient'}
              </button>
            </div>

            {/* New patient form */}
            {showCreatePatient && (
              <div className="p-4 bg-gray-50 rounded-md space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={newFirstName}
                      onChange={(e) => setNewFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={newLastName}
                      onChange={(e) => setNewLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleCreatePatient}
                  disabled={isCreatingPatient || !newFirstName.trim() || !newLastName.trim()}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreatingPatient ? 'Creating...' : 'Create Patient'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* File Upload */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Upload Images</h2>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            selectedPatient
              ? 'border-gray-300 hover:border-primary-400'
              : 'border-gray-200 bg-gray-50 cursor-not-allowed'
          }`}
          onClick={() => selectedPatient && document.getElementById('upload-input')?.click()}
        >
          <div className={selectedPatient ? 'text-gray-500' : 'text-gray-400'}>
            {selectedPatient ? (
              <>
                <p className="text-lg">Click to select images</p>
                <p className="text-sm mt-1">Or drag and drop files here</p>
              </>
            ) : (
              <p className="text-lg">Select a patient first</p>
            )}
          </div>
          <input
            id="upload-input"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
            disabled={!selectedPatient}
          />
        </div>

        {files.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Selected Files ({files.length})
            </h3>
            <ul className="space-y-2">
              {files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                >
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button
              onClick={handleUpload}
              disabled={isUploading || !selectedPatient}
              className="mt-4 w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading
                ? 'Uploading...'
                : `Upload ${files.length} Image${files.length > 1 ? 's' : ''} for ${selectedPatient?.first_name || 'Patient'}`}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Upload Guidelines</h2>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>- Use high-quality images (minimum 256x256 pixels)</li>
          <li>- Supported formats: PNG, JPG, JPEG, WebP</li>
          <li>- Images will be processed for CLIP embeddings</li>
          <li>- Patient data should be de-identified</li>
        </ul>
      </div>
    </div>
  )
}
